import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { Message } from '@gonfalon/messages/Message';
import { HelpTooltip } from '@gonfalon/ui/HelpTooltip';
import { Checkbox, FieldGroup } from '@launchpad-ui/components';
import clsx from 'clsx';

import { WindowFieldsRow } from '../../internal/CreateMetricModal/WindowFieldsRow/WindowFieldsRow';
import {
  getEndError,
  getStartError,
  WINDOW_UNITS,
  windowUnitLabel,
} from '../../internal/CreateMetricModal/windowingUtils';
import type { MetricForm, WindowUnit } from '../formUtils';

import styles from './MetricWindowingParams.module.css';

/** The measurement window's state and the write that turns it on or off. */
export function useMeasurementWindow(context: UseFormReturn<MetricForm>) {
  const { control, setValue, unregister } = context;
  const windowMode = useWatch({ control, name: 'windowMode' }) ?? 'none';

  return {
    isEnabled: windowMode === 'custom',
    setEnabled: (isEnabled: boolean) => {
      if (isEnabled) {
        setValue('windowMode', 'custom', { shouldDirty: true });
        setValue('windowStart', undefined, { shouldDirty: true });
        setValue('windowEnd', undefined, { shouldDirty: true });
      } else {
        setValue('windowMode', 'none', { shouldDirty: true });
        unregister(['windowStart', 'windowEnd'], { keepError: false, keepDirty: false, keepTouched: false });
      }
    },
  };
}

interface MeasurementWindowFieldsProps {
  context: UseFormReturn<MetricForm>;
  /** `section` drops the indent that aligns the fields under the flat layout's checkbox. */
  variant?: 'inline' | 'section';
}

/**
 * The window's unit and start/end offsets. Mount only while the window is on: mounting it
 * registers the offsets, which fail their required rule with no visible field to fix.
 */
export const MeasurementWindowFields: React.FC<MeasurementWindowFieldsProps> = ({ context, variant = 'inline' }) => {
  const { control, register, setValue, unregister, formState } = context;

  const windowStart = useWatch({ control, name: 'windowStart' });
  const windowEnd = useWatch({ control, name: 'windowEnd' });
  const windowUnit = useWatch({ control, name: 'windowUnit' }) ?? 'days';

  const unitDef = WINDOW_UNITS.find((u) => u.id === windowUnit) ?? WINDOW_UNITS[0];
  const unitLabel = windowUnitLabel(unitDef.id).toLowerCase();
  const maxInUnit = unitDef.maxInUnit;

  const startError = getStartError(windowStart, maxInUnit, unitLabel);
  const endError = getEndError(windowEnd, maxInUnit, unitLabel, windowStart);

  const startInteracted =
    Boolean(formState.touchedFields.windowStart || formState.dirtyFields.windowStart) || formState.isSubmitted;
  const endInteracted =
    Boolean(formState.touchedFields.windowEnd || formState.dirtyFields.windowEnd) || formState.isSubmitted;

  return (
    <div className={clsx(styles.windowingContent, variant === 'section' && styles.windowingContentInSection)}>
      <p className={styles.helpText}>
        <Message id="metrics.measurementWindow.help" />
      </p>

      <WindowFieldsRow
        key={windowUnit}
        windowUnit={windowUnit}
        windowStart={windowStart}
        windowEnd={windowEnd}
        showStartInvalid={Boolean(startError) && startInteracted}
        showEndInvalid={Boolean(endError) && endInteracted}
        startError={startError}
        endError={endError}
        unitLabel={windowUnit}
        maxInUnit={maxInUnit}
        variant={variant}
        onUnitChange={(unit) => {
          setValue('windowUnit', unit as WindowUnit, { shouldDirty: true });
          unregister(['windowStart', 'windowEnd'], {
            keepError: false,
            keepDirty: false,
            keepTouched: false,
          });
        }}
        startInputProps={register('windowStart', {
          valueAsNumber: true,
          validate: (val) => getStartError(val, maxInUnit, unitLabel) ?? true,
        })}
        endInputProps={register('windowEnd', {
          valueAsNumber: true,
          validate: (val, formValues) => getEndError(val, maxInUnit, unitLabel, formValues.windowStart) ?? true,
        })}
      />
    </div>
  );
};

/** The measurement window as a checkbox with its fields beneath: the layout the edit modal uses. */
export const MetricWindowingParams: React.FC<{ context: UseFormReturn<MetricForm> }> = ({ context }) => {
  const { isEnabled, setEnabled } = useMeasurementWindow(context);

  return (
    <FieldGroup className={styles.windowingSection}>
      <div className={styles.labelRow}>
        <Checkbox isSelected={isEnabled} onChange={setEnabled}>
          <Message id="metrics.measurementWindow.enableLabel" />
        </Checkbox>
        <HelpTooltip placement="right">
          <Message id="metrics.measurementWindow.description" />
        </HelpTooltip>
      </div>
      {isEnabled && <MeasurementWindowFields context={context} />}
    </FieldGroup>
  );
};

/** Convert form values to API millisecond offsets. Returns undefined for both when no window is set. */
export { toWindowOffsets } from '../../internal/CreateMetricModal/windowingUtils';
